import { createFormContext, zodResolver } from '@mantine/form'
import { type PropsWithChildren, useEffect } from 'react'

import { useMutationUpdateUserTaxProfile } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserTaxProfile } from '@flecto/types'
import { useNotifications } from '@flecto/ui'

import { useTaxesInfo } from '../../../taxes'
import { useTaxesSchema } from '../../hooks/useTaxesSchema'

export const [FormProvider, useTaxProfileFormContext, useForm] =
  createFormContext<Partial<CoreUserTaxProfile>>()

type TaxesFormProviderProps = PropsWithChildren<unknown> & {
  id?: string
  taxProfile?: Partial<CoreUserTaxProfile>
  onStart?: (taxProfile: CoreUserTaxProfile) => void
  onComplete?: (taxProfile: CoreUserTaxProfile) => void
}

export function TaxesFormProvider({
  children,
  taxProfile,
  onStart,
  onComplete,
  ...props
}: TaxesFormProviderProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_taxes' })
  const { mutateAsync: updateTaxProfile } = useMutationUpdateUserTaxProfile()
  const { showNotification } = useNotifications()
  const { schema, initialValues } = useTaxesSchema({ taxProfile })
  const { userDefaultTaxBracket, userDefaultTaxable } = useTaxesInfo()

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  async function saveTaxProfile() {
    try {
      onStart?.(form.values as CoreUserTaxProfile)
      await updateTaxProfile({ taxProfile: form.values })
      showNotification({
        type: 'success',
        message: t('notifications.success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notifications.error'),
      })
    } finally {
      onComplete?.(form.values as CoreUserTaxProfile)
    }
  }

  useEffect(() => {
    if (taxProfile) {
      form.setValues({
        ...taxProfile,
        default_tax_rate: userDefaultTaxBracket.value,
        default_taxable: userDefaultTaxable,
      })
    } else {
      form.reset()
    }
  }, [taxProfile])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => saveTaxProfile())}>
        {children}
      </form>
    </FormProvider>
  )
}
